// src/views/admin/ADCartes.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import CarteScolaireCard from "./CarteScolaireCard"; 
import "./cartes.css"; 

/* ============================================================================
 * CONSTANTES
 * ========================================================================== */

const initiales = (prenom, nom) => `${prenom?.[0] || ""}${nom?.[0] || ""}`.toUpperCase();

/* ============================================================================
 * HOOK — état + logique métier
 * ========================================================================== */

function useCartesAdmin(slug) {
  const toast = useToastHook();
  const {
    getAnneesScolaires,
    getClasses,
    getEleves,
    genererCartesParClasse,
    genererCartesParEleves,
    telechargerCartesPDF,
  } = useAdminService();

  const [annees, setAnnees] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [classes, setClasses] = useState([]);
  const [classeFilt, setClasseFilt] = useState("");

  // Tous les élèves sont chargés une seule fois puis filtrés côté client
  // (comme dans l'ADCartes d'origine) — évite de dépendre d'un filtre
  // classe_id côté API qui n'existe peut-être pas.
  const [allEleves, setAllEleves] = useState(null);
  const [loadingEleves, setLoadingEleves] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [cartes, setCartes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  // Mémorise COMMENT la dernière génération a été faite (classe entière ou
  // sélection précise d'élèves), car les cartes ne sont pas persistées en
  // base — c'est ce payload qu'on renvoie tel quel au téléchargement/impression.
  const [derniereGeneration, setDerniereGeneration] = useState(null); // { classe_id } ou { eleves }
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    if (slug) init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (slug && anneeFilt) loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, anneeFilt]);

  useEffect(() => {
    setSelectedIds([]);
    setSearch("");
    setCartes([]);
    setDerniereGeneration(null);
  }, [classeFilt]);

  const init = async () => {
    setLoading(true);
    try {
      const anneesData = await getAnneesScolaires(slug, { show_archived: "false" });
      setAnnees(anneesData);
      const anneeActive = anneesData.find((a) => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les années scolaires");
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    try {
      const classesData = await getClasses(slug, { show_archived: "false" });
      setClasses(classesData.filter((c) => String(c.annee_scolaire) === String(anneeFilt)));
      setClasseFilt("");
    } catch (error) {
      toast.error("Impossible de charger les classes");
    }
  };

  const ensureElevesLoaded = async () => {
    if (allEleves !== null) return;
    setLoadingEleves(true);
    try {
      const data = await getEleves(slug, { page_size: "all" });
      setAllEleves(data.results || data || []);
    } catch (error) {
      toast.error("Impossible de charger la liste des élèves");
      setAllEleves([]);
    } finally {
      setLoadingEleves(false);
    }
  };

  useEffect(() => {
    if (slug && classeFilt) ensureElevesLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, classeFilt]);

  const eleveClasseId = (eleve) => {
    const insc = (eleve.inscriptions || []).find((i) => String(i.annee_scolaire) === String(anneeFilt));
    return insc?.classe ?? null;
  };
  const eleveClasseNom = (eleve) => {
    const insc = (eleve.inscriptions || []).find((i) => String(i.annee_scolaire) === String(anneeFilt));
    return insc?.classe_nom ?? "Non inscrit";
  };

  const elevesFiltres = (allEleves || []).filter((e) => {
    const matchClasse = !classeFilt || String(eleveClasseId(e)) === String(classeFilt);
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      `${e.prenom} ${e.nom}`.toLowerCase().includes(q) ||
      e.matricule?.toLowerCase().includes(q);
    return matchClasse && matchSearch;
  });

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectAllVisible = () =>
    setSelectedIds((prev) =>
      prev.length === elevesFiltres.length ? [] : elevesFiltres.map((e) => e.id)
    );

  const carteGenereeMatricules = new Set((cartes || []).map((c) => c.matricule));
  const eleveACarteGeneree = (e) => carteGenereeMatricules.has(e.matricule);

  // ── Génération ──────────────────────────────────────────────────────
  const handleGenererParClasse = async () => {
    if (!classeFilt) {
      toast.error("Choisis une classe");
      return;
    }
    setWorking(true);
    try {
      const data = await genererCartesParClasse(slug, classeFilt, anneeFilt);
      setCartes(data);
      setDerniereGeneration({ classe_id: classeFilt });
      if (data.length === 0) toast.error("Aucun élève inscrit dans cette classe pour cette année");
      else toast.success(`${data.length} carte(s) générée(s)`);
    } catch (error) {
      toast.error("Impossible de générer les cartes (vérifie qu'une configuration de carte est active)");
    } finally {
      setWorking(false);
    }
  };

  const handleGenererSelection = async () => {
    if (selectedIds.length === 0) {
      toast.error("Sélectionne au moins un élève");
      return;
    }
    setWorking(true);
    try {
      const data = await genererCartesParEleves(slug, selectedIds, anneeFilt);
      setCartes(data);
      setDerniereGeneration({ eleves: [...selectedIds] });
      if (data.length === 0) toast.error("Aucune carte générée pour cette sélection");
      else toast.success(`${data.length} carte(s) générée(s)`);
    } catch (error) {
      toast.error("Impossible de générer les cartes pour cette sélection");
    } finally {
      setWorking(false);
    }
  };

  // ── PDF / Impression (génération serveur via WeasyPrint) ────────────
  // On renvoie le même payload que celui utilisé pour la dernière génération
  // (classe entière ou sélection précise), pour rester cohérent avec
  // l'aperçu affiché à l'écran.
  const payloadPourTelechargement = () => ({
    ...(derniereGeneration || {}),
    annee_scolaire: anneeFilt,
  });

  const handleDownloadPDF = async () => {
    if (cartes.length === 0) {
      toast.error("Aucune carte à exporter");
      return;
    }
    setGeneratingPDF(true);
    try {
      const blob = await telechargerCartesPDF(slug, payloadPourTelechargement());
      const dateGen = new Date().toLocaleDateString("fr-FR").replace(/\//g, "-");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cartes_scolaires_${dateGen}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF généré avec succès");
    } catch (err) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

const handleImprimer = async () => {
  if (cartes.length === 0) {
    toast.error("Aucune carte à imprimer");
    return;
  }
  // Ouvrir l'onglet AVANT l'await, tant qu'on est encore dans le geste
  // utilisateur — sinon le navigateur bloque le popup une fois la requête
  // terminée (c'est le vrai bug : aucune erreur, ça ne fait juste rien).
  const nouvelOnglet = window.open("", "_blank");
  setGeneratingPDF(true);
  try {
    const blob = await telechargerCartesPDF(slug, payloadPourTelechargement());
    const url = window.URL.createObjectURL(blob);
    if (nouvelOnglet) {
      nouvelOnglet.location.href = url;
    } else {
      toast.error("Le navigateur a bloqué l'ouverture de l'onglet — autorise les pop-ups pour ce site");
    }
  } catch (err) {
    toast.error("Impossible d'imprimer les cartes");
    if (nouvelOnglet) nouvelOnglet.close();
  } finally {
    setGeneratingPDF(false);
  }
};

  return {
    annees, anneeFilt, setAnneeFilt,
    classes, classeFilt, setClasseFilt,
    loading, loadingEleves, working,
    search, setSearch, elevesFiltres, selectedIds,
    toggleSelect, selectAllVisible, eleveACarteGeneree,
    cartes, handleGenererParClasse, handleGenererSelection,
    generatingPDF, handleDownloadPDF, handleImprimer,
  };
}

/* ============================================================================
 * UI — briques d'affichage
 * ========================================================================== */

/* ── 1. Sélecteur classe ──────────────────────────────────────────────── */

function ClasseSelector({ annees, anneeFilt, onAnneeChange, classes, classeFilt, onClasseChange }) {
  return (
    <div className="toolbar">
      <div className="sb2" style={{ flexWrap: "wrap" }}>
        <select value={anneeFilt} onChange={(e) => onAnneeChange(e.target.value)}>
          {annees.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nom}{a.actif ? " (active)" : ""}
            </option>
          ))}
        </select>
        <select value={classeFilt} onChange={(e) => onClasseChange(e.target.value)}>
          <option value="">-- Choisir une classe --</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>{c.nom}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* ── 2. Recherche + compteur de sélection ────────────────────────────── */

function SelectionBar({ search, onSearchChange, selectedCount, total }) {
  return (
    <div className="toolbar">
      <div className="selection-bar">
        <div className="sb2">
          <i className="ti ti-search" style={{ color: "var(--ink-soft)" }} />
          <input
            type="text"
            placeholder="Rechercher nom, prénom, matricule…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className="sb2">
          <span className="selection-count">
            <i className="ti ti-user-check" />{selectedCount} / {total} sélectionné(s)
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── 3. Liste des élèves en tableau — même gabarit que ADEleves ──────── */

function ElevesList({ eleves, selectedIds, onToggleSelect, eleveACarteGeneree, loading }) {
  if (loading) return <Loader />;

  if (eleves.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <strong>Aucun élève trouvé</strong>
          Choisis une classe contenant des élèves pour générer leurs cartes.
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 12 }}>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: "8%" }}></th>
            <th style={{ width: "26%" }}>Matricule</th>
            <th style={{ width: "36%" }}>Nom complet</th>
            <th style={{ width: "30%" }}>Statut</th>
          </tr>
        </thead>
        <tbody>
          {eleves.map((e) => {
            const isSelected = selectedIds.includes(e.id);
            const genere = eleveACarteGeneree(e);
            return (
              <tr
                key={e.id}
                className={genere ? "is-generee" : ""}
                onClick={() => onToggleSelect(e.id)}
                style={{ cursor: "pointer" }}
              >
                <td style={{ textAlign: "center" }} onClick={(ev) => ev.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(e.id)}
                  />
                </td>
                <td>
                  <span style={{
                    fontFamily: "'Space Grotesk',sans-serif",
                    fontWeight: 700, fontSize: 12,
                    background: "var(--bl-bg)", color: "var(--bl)",
                    padding: "3px 9px", borderRadius: 6
                  }}>
                    {e.matricule}
                  </span>
                </td>
                <td style={{ fontWeight: 500 }}>{e.prenom} {e.nom}</td>
                <td>
                  {genere
                    ? <span className="pill ok">Carte générée</span>
                    : <span className="pill">Non générée</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── 4. Aperçu imprimable des cartes générées ────────────────────────── */

function CartesPreviewArea({ cartes, onDownloadPDF, onImprimer, generatingPDF }) {
  if (cartes.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="preview-area-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className="ti ti-id-badge-2" />
          <h3>Cartes générées ({cartes.length})</h3>
        </div>
        <div className="export-actions" style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            className="bsec"
            onClick={onDownloadPDF}
            disabled={generatingPDF}
            style={{ color: "#dc3545", borderColor: "#dc3545" }}
          >
            <i className="ti ti-file-pdf" /> {generatingPDF ? "Génération..." : "Télécharger PDF"}
          </button>
          <button
            className="bsec"
            onClick={onImprimer}
            disabled={generatingPDF}
            style={{ color: "#6f42c1", borderColor: "#6f42c1" }}
          >
            <i className="ti ti-printer" /> {generatingPDF ? "Génération..." : "Imprimer"}
          </button>
        </div>
      </div>

      <div className="cartes-preview-area">
        {cartes.map((carte, i) => (
          <div key={carte.matricule || i} className="carte-page-wrap">
            <CarteScolaireCard carte={carte} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 5. Barre d'actions globale, fixée en bas ────────────────────────── */

function StickyActionBar({ total, onPrint, generatingPDF }) {
  if (!total) return null;
  return (
    <div className="sticky-actionbar">
      <button className="bprim" onClick={onPrint} disabled={generatingPDF}>
        <i className="ti ti-printer" />
        {generatingPDF ? "Génération en cours…" : `Imprimer ${total} carte(s)`}
      </button>
      <div className="sticky-actionbar-note">
        {total} carte(s) générée(s) — prêtes à imprimer
      </div>
    </div>
  );
}

/* ============================================================================
 * COMPOSANT PRINCIPAL
 *
 * Classement des blocs (identique à ADBulletins) :
 *   1. En-tête
 *   2. Sélecteur de classe (+ état vide si rien de sélectionné)
 *   3. Bandeau d'aide + bouton de génération globale
 *   4. Recherche + sélection (tout sélectionner / générer pour la sélection)
 *   5. Liste des élèves en tableau (checkbox, matricule, nom, statut)
 *   6. Aperçu imprimable des cartes générées (+ Télécharger PDF / Imprimer)
 *   7. Barre d'actions globale fixée en bas
 * ========================================================================== */

export default function ADCartes({ slug }) {
  const s = useCartesAdmin(slug);

  if (s.loading) return <Loader />;

  const toutSelectionne = s.selectedIds.length === s.elevesFiltres.length && s.elevesFiltres.length > 0;

  return (
    <div>

      {/* 2. Sélecteur de classe -------------------------------------------- */}
      <ClasseSelector
        annees={s.annees} anneeFilt={s.anneeFilt} onAnneeChange={s.setAnneeFilt}
        classes={s.classes} classeFilt={s.classeFilt} onClasseChange={s.setClasseFilt}
      />

      {!s.classeFilt && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="empty-state">
            <strong>Aucune classe sélectionnée</strong>
            Choisis une classe pour commencer la génération des cartes.
          </div>
        </div>
      )}

      {s.classeFilt && (
        <>
          {/* 4. Recherche --------------------------------------------------- */}
          <SelectionBar
            search={s.search} onSearchChange={s.setSearch}
            selectedCount={s.selectedIds.length} total={s.elevesFiltres.length}
          />

          {/* Barre d'actions — génération globale + sélection, même emplacement */}
          {s.elevesFiltres.length > 0 && (
            <div className="sb2" style={{ marginTop: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <button className="bprim" onClick={s.handleGenererParClasse} disabled={s.working}>
                <i className="ti ti-users" />
                {s.working ? "Génération en cours…" : "Générer les cartes pour toute la classe"}
              </button>
              <button className="bsec" onClick={s.selectAllVisible}>
                <i className={`ti ti-${toutSelectionne ? "square" : "checkbox"}`} />
                {toutSelectionne ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
              {s.selectedIds.length > 0 && (
                <button className="bprim" onClick={s.handleGenererSelection} disabled={s.working}>
                  <i className="ti ti-id-badge-2" />Générer pour {s.selectedIds.length} élève(s) sélectionné(s)
                </button>
              )}
            </div>
          )}

          {/* 5. Liste des élèves (tableau) ------------------------------------ */}
          <ElevesList
            eleves={s.elevesFiltres}
            selectedIds={s.selectedIds}
            onToggleSelect={s.toggleSelect}
            eleveACarteGeneree={s.eleveACarteGeneree}
            loading={s.loadingEleves}
          />

          {/* 6. Aperçu imprimable des cartes ---------------------------------- */}
          <CartesPreviewArea
            cartes={s.cartes}
            onDownloadPDF={s.handleDownloadPDF}
            onImprimer={s.handleImprimer}
            generatingPDF={s.generatingPDF}
          />
        </>
      )}

      {/* 7. Barre d'actions globale, fixée en bas --------------------------- */}
      <StickyActionBar
        total={s.cartes.length}
        onPrint={s.handleImprimer}
        generatingPDF={s.generatingPDF}
      />
    </div>
  );
}